import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useBulkDeleteAdminSmsMutation,
  useBulkResendAdminSmsMutation,
  useGetAdminSmsHistoryQuery,
  useResendAdminSmsViaProviderMutation,
} from "../../../store/smsMessagingAdminApi";

function formatSentAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "sent" || s === "delivered") return "bg-green-100 text-green-800";
  if (s === "failed") return "bg-red-100 text-red-800";
  if (s === "pending") return "bg-orange-100 text-orange-800";
  return "bg-gray-100 text-gray-700";
}

function providerLabel(id, catalog = []) {
  const found = catalog.find((p) => p.id === id);
  return found?.label || id || "Unknown";
}

function previewMessage(message, max = 72) {
  const text = String(message || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

export default function SmsDeliveryHistory() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [sourceType, setSourceType] = useState("all");
  const [provider, setProvider] = useState("all");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [appliedPhone, setAppliedPhone] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [resendProvider, setResendProvider] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(null); // 'resend' | 'delete' | null
  const [bulkProvider, setBulkProvider] = useState("");

  const queryArgs = useMemo(
    () => ({
      page,
      limit: 20,
      status,
      source_type: sourceType,
      provider,
      recipient_phone: appliedPhone,
      date_from: dateFrom,
      date_to: dateTo,
    }),
    [page, status, sourceType, provider, appliedPhone, dateFrom, dateTo]
  );

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetAdminSmsHistoryQuery(queryArgs);
  const [resendSms, { isLoading: isResending }] =
    useResendAdminSmsViaProviderMutation();
  const [bulkResendSms, { isLoading: isBulkResending }] =
    useBulkResendAdminSmsMutation();
  const [bulkDeleteSms, { isLoading: isBulkDeleting }] =
    useBulkDeleteAdminSmsMutation();

  const logs = Array.isArray(data?.logs) ? data.logs : [];
  const pagination = data?.pagination || {};
  const smsProviders = Array.isArray(data?.sms_providers)
    ? data.sms_providers
    : [];

  useEffect(() => {
    setSelectedIds([]);
  }, [page, status, sourceType, provider, appliedPhone, dateFrom, dateTo]);

  const pageIds = useMemo(() => logs.map((log) => log.id).filter(Boolean), [logs]);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected =
    pageIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

  const resendOptions = useMemo(() => {
    if (!selected) return [];
    if (Array.isArray(selected.resend_providers) && selected.resend_providers.length) {
      return selected.resend_providers;
    }
    const original = selected.provider;
    return smsProviders.filter((p) => p.id !== original);
  }, [selected, smsProviders]);

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const togglePage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const openResend = (log) => {
    setSelected(log);
    const options =
      Array.isArray(log.resend_providers) && log.resend_providers.length
        ? log.resend_providers
        : smsProviders.filter((p) => p.id !== log.provider);
    setResendProvider(options[0]?.id || "");
  };

  const openBulkResend = () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one SMS log.");
      return;
    }
    setBulkProvider(smsProviders[0]?.id || "");
    setBulkMode("resend");
  };

  const openBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one SMS log.");
      return;
    }
    setBulkMode("delete");
  };

  const handleResend = async () => {
    if (!selected?.id) return;
    if (!resendProvider) {
      toast.error("Select a provider to resend with.");
      return;
    }
    if (selected.provider && selected.provider === resendProvider) {
      toast.error("Choose a different provider than the original send.");
      return;
    }
    try {
      await resendSms({
        logId: selected.id,
        provider: resendProvider,
      }).unwrap();
      toast.success(
        `Queued resend via ${providerLabel(resendProvider, smsProviders)}`
      );
      setSelected(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Resend failed.");
    }
  };

  const handleBulkResend = async () => {
    if (!bulkProvider) {
      toast.error("Select a provider to resend with.");
      return;
    }
    try {
      const result = await bulkResendSms({
        ids: selectedIds,
        provider: bulkProvider,
      }).unwrap();
      const payload = result?.data || result;
      const ok = payload?.succeeded?.length ?? 0;
      const fail = payload?.failed?.length ?? 0;
      if (fail === 0) {
        toast.success(
          `Queued ${ok} SMS via ${providerLabel(bulkProvider, smsProviders)}`
        );
      } else {
        toast.error(
          `Queued ${ok}; ${fail} failed. Check that each log uses a different original provider.`
        );
      }
      setBulkMode(null);
      setSelectedIds([]);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Bulk resend failed.");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const result = await bulkDeleteSms({ ids: selectedIds }).unwrap();
      const payload = result?.data || result;
      const deleted = payload?.deleted ?? selectedIds.length;
      toast.success(`Deleted ${deleted} SMS log(s).`);
      setBulkMode(null);
      setSelectedIds([]);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Bulk delete failed.");
    }
  };

  const loadError =
    isError &&
    (error?.data?.message || error?.message || "Could not load SMS history.");

  const busy = isResending || isBulkResending || isBulkDeleting;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center">
          <Link to="/sms-messaging" aria-label="Back to SMS messaging">
            <BsArrowLeft className="text-xl text-gray-600 mr-4 cursor-pointer" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#45464e]">
              SMS delivery log
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              View message content, resend via another gateway, or delete logs
              in bulk. Cooperative wallets are not charged for admin retries.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Source
          </label>
          <select
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="event">Event</option>
            <option value="communication_message">Communication</option>
            <option value="birthday">Birthday</option>
            <option value="eminute">E-Minute</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Provider
          </label>
          <select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            {smsProviders.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Phone search
          </label>
          <div className="flex gap-2">
            <input
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedPhone(phoneSearch.trim());
                  setPage(1);
                }
              }}
              placeholder="e.g. 0803"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => {
                setAppliedPhone(phoneSearch.trim());
                setPage(1);
              }}
              className="px-3 py-2 rounded-md bg-[#31778c] text-white text-sm"
            >
              Search
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-3 mb-4 flex flex-wrap items-center justify-between gap-3 border border-[#31778c]/30">
          <p className="text-sm text-[#45464e]">
            <strong>{selectedIds.length}</strong> selected
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openBulkResend}
              disabled={busy}
              className="px-3 py-2 rounded-md bg-[#8b313a] text-white text-sm font-medium disabled:opacity-50"
            >
              Resend selected…
            </button>
            <button
              type="button"
              onClick={openBulkDelete}
              disabled={busy}
              className="px-3 py-2 rounded-md border border-red-300 text-red-700 text-sm font-medium disabled:opacity-50"
            >
              Delete selected…
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {loadError && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 text-red-600 text-sm">
          {loadError}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#383838] text-white">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected;
                    }}
                    onChange={togglePage}
                    disabled={pageIds.length === 0}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Sent at</th>
                <th className="text-left px-4 py-3 font-semibold">Cooperative</th>
                <th className="text-left px-4 py-3 font-semibold">Recipient</th>
                <th className="text-left px-4 py-3 font-semibold">Message</th>
                <th className="text-left px-4 py-3 font-semibold">Provider</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {(isLoading || isFetching) && logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Loading SMS logs…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No SMS logs found for these filters.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select SMS log ${log.id}`}
                        checked={selectedIds.includes(log.id)}
                        onChange={() => toggleOne(log.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {formatSentAt(log.sent_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#45464e]">
                        {log.company_name || `Company #${log.company_id}`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {log.branch_name || `Branch #${log.branch_id}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{log.recipient_name || "—"}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {log.recipient_phone}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="text-gray-700 truncate" title={log.message || ""}>
                        {previewMessage(log.message)}
                      </div>
                      <button
                        type="button"
                        onClick={() => setViewing(log)}
                        className="text-xs font-medium text-[#31778c] hover:underline mt-0.5"
                      >
                        View full message
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {providerLabel(log.provider, smsProviders)}
                      {log.resent_from_log_id ? (
                        <div className="text-xs text-[#31778c]">
                          Resend of #{log.resent_from_log_id}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(
                          log.status
                        )}`}
                      >
                        {String(log.status || "unknown").toUpperCase()}
                      </span>
                      {log.error_message ? (
                        <div
                          className="text-xs text-red-600 mt-1 max-w-[180px] truncate"
                          title={log.error_message}
                        >
                          {log.error_message}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {String(log.source_type || "other").replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openResend(log)}
                        disabled={
                          !log.resend_providers?.length && smsProviders.length < 2
                        }
                        className="text-sm font-medium text-[#8b313a] hover:underline disabled:opacity-40 disabled:no-underline"
                      >
                        Resend via other…
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
            <span className="text-gray-600">
              Page {pagination.current_page || page} of {pagination.last_page}{" "}
              ({pagination.total || 0} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={(pagination.current_page || page) <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={
                  (pagination.current_page || page) >= (pagination.last_page || 1)
                }
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-bold text-[#45464e] mb-1">
              Message content
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              {viewing.recipient_name || "Recipient"} ·{" "}
              <span className="font-mono text-xs">{viewing.recipient_phone}</span>
            </p>
            <div className="mb-4 text-sm bg-gray-50 rounded-md p-4 border border-gray-100 whitespace-pre-wrap break-words text-gray-800 max-h-[50vh] overflow-y-auto">
              {viewing.message || "No message body stored for this log."}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setViewing(null);
                  openResend(viewing);
                }}
                className="px-4 py-2 rounded-md border border-[#8b313a] text-[#8b313a] text-sm"
              >
                Resend…
              </button>
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="px-4 py-2 rounded-md bg-[#31778c] text-white text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-bold text-[#45464e] mb-1">
              Resend via another provider
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Original provider:{" "}
              <strong>
                {providerLabel(selected.provider, smsProviders) || "Unknown"}
              </strong>
              . Cooperative wallet is not charged again for this admin retry.
            </p>
            <div className="mb-3 text-sm bg-gray-50 rounded-md p-3 border border-gray-100">
              <div className="font-mono text-xs text-gray-500 mb-1">
                {selected.recipient_phone}
              </div>
              <div className="text-gray-800 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                {selected.message}
              </div>
            </div>
            <label className="block text-sm font-medium text-[#45464e] mb-1">
              Send with
            </label>
            <select
              value={resendProvider}
              onChange={(e) => setResendProvider(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-5"
            >
              {resendOptions.length === 0 ? (
                <option value="">No alternate providers</option>
              ) : (
                resendOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))
              )}
            </select>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                disabled={isResending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || !resendProvider}
                className="px-4 py-2 rounded-md bg-[#8b313a] text-white text-sm font-medium disabled:opacity-50"
              >
                {isResending ? "Queueing…" : "Resend SMS"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkMode === "resend" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-bold text-[#45464e] mb-1">
              Resend {selectedIds.length} selected SMS
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Each log must use a different provider than its original send.
              Logs that cannot be resent are reported without stopping the rest.
              Wallets are not charged again.
            </p>
            <label className="block text-sm font-medium text-[#45464e] mb-1">
              Send with
            </label>
            <select
              value={bulkProvider}
              onChange={(e) => setBulkProvider(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-5"
            >
              {smsProviders.length === 0 ? (
                <option value="">No providers available</option>
              ) : (
                smsProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))
              )}
            </select>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setBulkMode(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                disabled={isBulkResending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkResend}
                disabled={isBulkResending || !bulkProvider}
                className="px-4 py-2 rounded-md bg-[#8b313a] text-white text-sm font-medium disabled:opacity-50"
              >
                {isBulkResending ? "Queueing…" : "Resend all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkMode === "delete" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-bold text-[#45464e] mb-1">
              Delete {selectedIds.length} SMS log(s)?
            </h2>
            <p className="text-sm text-gray-600 mb-5">
              This permanently removes the selected delivery log entries. It
              does not recall messages already delivered to handsets.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setBulkMode(null)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {isBulkDeleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
